import { zonedParts } from '@nucleus-commerce/core/dates';

/**
 * Wall-clock calendar maths on plain integers. Values never pass through `new Date(string)`, so
 * the browser's timezone can never shift a day; weekdays come from `Date` in UTC.
 */

/** A calendar day; `month` is 1–12. */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

/** A time of day on a 24-hour clock. */
export interface WallTime {
  hour: number;
  minute: number;
}

export interface CalendarMonth {
  year: number;
  month: number;
}

/** Years the pickers handle; the value format has four year digits. */
export const MIN_YEAR = 1;
export const MAX_YEAR = 9999;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/;

export const pad = (value: number, width = 2) => String(value).padStart(width, '0');

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

export function isValidDate(date: CalendarDate): boolean {
  const { year, month, day } = date;
  return (
    Number.isInteger(year) &&
    Number.isInteger(month) &&
    Number.isInteger(day) &&
    year >= MIN_YEAR &&
    year <= MAX_YEAR &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth(year, month)
  );
}

export function isValidTime(time: WallTime): boolean {
  return (
    Number.isInteger(time.hour) &&
    Number.isInteger(time.minute) &&
    time.hour >= 0 &&
    time.hour <= 23 &&
    time.minute >= 0 &&
    time.minute <= 59
  );
}

/** `YYYY-MM-DD` → parts; null for anything else or a day that does not exist. */
export function parseDate(value: string | null | undefined): CalendarDate | null {
  const match = DATE_RE.exec(value ?? '');
  if (!match) return null;
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  return isValidDate(date) ? date : null;
}

/** `HH:mm`, optionally with seconds (ignored) as native time values may carry them. */
export function parseTime(value: string | null | undefined): WallTime | null {
  const match = TIME_RE.exec(value ?? '');
  if (!match) return null;
  const time = { hour: Number(match[1]), minute: Number(match[2]) };
  if (match[3] !== undefined && Number(match[3]) > 59) return null;
  return isValidTime(time) ? time : null;
}

/** `YYYY-MM-DDTHH:mm` (seconds tolerated, as for `parseTime`). */
export function parseDateTime(
  value: string | null | undefined,
): { date: CalendarDate; time: WallTime } | null {
  const [datePart, timePart, ...rest] = (value ?? '').split('T');
  if (rest.length > 0 || timePart === undefined) return null;
  const date = parseDate(datePart);
  const time = parseTime(timePart);
  return date && time ? { date, time } : null;
}

export function formatDate(date: CalendarDate): string {
  return `${pad(date.year, 4)}-${pad(date.month)}-${pad(date.day)}`;
}

export function formatTime(time: WallTime): string {
  return `${pad(time.hour)}:${pad(time.minute)}`;
}

export function formatDateTime(date: CalendarDate, time: WallTime): string {
  return `${formatDate(date)}T${formatTime(time)}`;
}

/** Sortable number for a day (`20261023`). */
export const dateKey = (date: CalendarDate) => date.year * 10_000 + date.month * 100 + date.day;
/** Minutes since midnight. */
export const timeKey = (time: WallTime) => time.hour * 60 + time.minute;
export const monthKey = (month: CalendarMonth) => month.year * 12 + (month.month - 1);

export const sameDate = (a: CalendarDate | null, b: CalendarDate | null) =>
  !!a && !!b && dateKey(a) === dateKey(b);

/** A UTC `Date` at midnight of the day; `setUTCFullYear` keeps years below 100 as typed. */
function toUtc(date: CalendarDate): Date {
  const utc = new Date(0);
  utc.setUTCFullYear(date.year, date.month - 1, date.day);
  return utc;
}

function fromUtc(utc: Date): CalendarDate {
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}

/** Sunday = 0 … Saturday = 6. */
export function dayOfWeek(date: CalendarDate): number {
  return toUtc(date).getUTCDay();
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const utc = toUtc(date);
  utc.setUTCDate(utc.getUTCDate() + days);
  return fromUtc(utc);
}

/** Same day of month `months` later, clamped to the month's length (Jan 31 + 1 → Feb 28). */
export function addMonths(date: CalendarDate, months: number): CalendarDate {
  const index = date.year * 12 + (date.month - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

export function addMonthsToMonth(value: CalendarMonth, months: number): CalendarMonth {
  const index = monthKey(value) + months;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** The first day of the week holding `date`, for a week starting on `weekStart` (0 = Sunday). */
export function startOfWeek(date: CalendarDate, weekStart: number): CalendarDate {
  return addDays(date, -((dayOfWeek(date) - weekStart + 7) % 7));
}

/** Six full weeks covering the month, starting on the locale's first day of the week. */
export function monthGrid(month: CalendarMonth, weekStart: number): CalendarDate[][] {
  const first = startOfWeek({ year: month.year, month: month.month, day: 1 }, weekStart);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(first, week * 7 + day)),
  );
}

export function isDateInRange(
  date: CalendarDate,
  min: CalendarDate | null,
  max: CalendarDate | null,
): boolean {
  const key = dateKey(date);
  return (
    key >= dateKey({ year: MIN_YEAR, month: 1, day: 1 }) &&
    key <= dateKey({ year: MAX_YEAR, month: 12, day: 31 }) &&
    (!min || key >= dateKey(min)) &&
    (!max || key <= dateKey(max))
  );
}

export function clampDate(
  date: CalendarDate,
  min: CalendarDate | null,
  max: CalendarDate | null,
): CalendarDate {
  const lower = min ?? { year: MIN_YEAR, month: 1, day: 1 };
  const upper = max ?? { year: MAX_YEAR, month: 12, day: 31 };
  if (dateKey(date) < dateKey(lower)) return lower;
  if (dateKey(date) > dateKey(upper)) return upper;
  return date;
}

/** Whether any day of the month lies within [min, max]. */
export function isMonthInRange(
  month: CalendarMonth,
  min: CalendarDate | null,
  max: CalendarDate | null,
): boolean {
  if (month.year < MIN_YEAR || month.year > MAX_YEAR) return false;
  const key = monthKey(month);
  return (!min || key >= monthKey(min)) && (!max || key <= monthKey(max));
}

export function isTimeInRange(time: WallTime, min: WallTime | null, max: WallTime | null) {
  const key = timeKey(time);
  return (!min || key >= timeKey(min)) && (!max || key <= timeKey(max));
}

export function clampTime(time: WallTime, min: WallTime | null, max: WallTime | null): WallTime {
  if (min && timeKey(time) < timeKey(min)) return min;
  if (max && timeKey(time) > timeKey(max)) return max;
  return time;
}

/**
 * The current wall-clock date and time, on `timeZone`'s clock when given (the store's or the
 * schedule's), otherwise on the browser's. An unknown zone falls back to the browser's clock.
 */
export function nowIn(
  timeZone?: string,
  now: Date = new Date(),
): {
  date: CalendarDate;
  time: WallTime;
} {
  if (timeZone) {
    try {
      const p = zonedParts(now, timeZone);
      return {
        date: { year: p.year, month: p.month, day: p.day },
        time: { hour: p.hour, minute: p.minute },
      };
    } catch {
      // An unknown or half-typed zone: use the browser's clock.
    }
  }
  return {
    date: { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() },
    time: { hour: now.getHours(), minute: now.getMinutes() },
  };
}
